import { useCallback, useEffect, useRef } from "react"
import { useSpeechToText } from "@/modules/speech-to-text/hooks/useSpeechToText"
import { ConnectionStatus, TranscriptEntry } from "@/modules/speech-to-text/types"
import { useCreateOrAppendCallTranscriptMutation } from "@/modules/video-call/services/useCreateOrAppendCallTranscriptMutation"
import { SofiaEnvironment } from "@/sofia-environment"
import {
  usePhoneCallTranscriptionStore,
  usePhoneCallTranscriptionStoreApi,
} from "@/stores/usePhoneCallTranscriptionStore"
import { Language } from "@/types/__generated__/union-types"

export interface UsePhoneCallTranscriptionOptions {
  appointmentId: string
  progressNoteId: string | null
  language: Language
  enabled: boolean
}

export interface UsePhoneCallTranscriptionReturn {
  isRecording: boolean
  connectionStatus: ConnectionStatus
  startTranscription: () => Promise<void>
  stopTranscription: () => Promise<void>
  transcripts: TranscriptEntry[]
  selectedMicrophoneId: string | null
  setSelectedMicrophoneId: (microphoneId: string | null) => void
}

export const usePhoneCallTranscription = (
  options: UsePhoneCallTranscriptionOptions,
): UsePhoneCallTranscriptionReturn => {
  const { appointmentId, progressNoteId, language, enabled } = options

  const isRecording = usePhoneCallTranscriptionStore((state) => state.isRecording)
  const connectionStatus = usePhoneCallTranscriptionStore((state) => state.connectionStatus)
  const transcripts = usePhoneCallTranscriptionStore((state) => state.transcripts)
  const selectedMicrophoneId = usePhoneCallTranscriptionStore((state) => state.selectedMicrophoneId)

  const stopRecordingAction = usePhoneCallTranscriptionStore((state) => state.stopRecording)
  const addTranscript = usePhoneCallTranscriptionStore((state) => state.addTranscript)
  const clearTranscripts = usePhoneCallTranscriptionStore((state) => state.clearTranscripts)
  const setConnectionStatusAction = usePhoneCallTranscriptionStore((state) => state.setConnectionStatus)
  const setSelectedMicrophoneIdAction = usePhoneCallTranscriptionStore((state) => state.setSelectedMicrophoneId)
  const reset = usePhoneCallTranscriptionStore((state) => state.reset)

  const store = usePhoneCallTranscriptionStoreApi()
  const [commitMutation] = useCreateOrAppendCallTranscriptMutation()

  const transcriptionStartedAtRef = useRef<string | null>(null)
  const saveIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastSavedTranscriptIndexRef = useRef(0)
  const callTranscriptIdRef = useRef<string | null>(null)
  const hasStartedRef = useRef(false)

  const speechToText = useSpeechToText({
    language,
    audioSource: "microphone",
    speaker: "AGENT",
    deviceId: selectedMicrophoneId,
    enabled,
    onTranscript: (text, isFinal) => {
      if (isFinal) {
        addTranscript({
          text,
          timestamp: Date.now(),
          isFinal: true,
          speaker: "AGENT",
        })
      }
    },
    onStatusChange: (status) => {
      setConnectionStatusAction(status)
    },
  })

  const saveTranscripts = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      const allTranscripts = store.getState().transcripts
      const newTranscripts = allTranscripts.slice(lastSavedTranscriptIndexRef.current)

      if (newTranscripts.length === 0 || !progressNoteId) {
        resolve()
        return
      }

      const transcriptEntries = newTranscripts.map((t) => ({
        id: t.id,
        text: t.text,
        timestamp: t.timestamp,
        isFinal: t.isFinal,
        speaker: t.speaker,
      }))

      commitMutation({
        variables: {
          input: {
            id: callTranscriptIdRef.current,
            appointmentId,
            progressNoteId,
            transcript: transcriptEntries,
            transcriptStartedAt: transcriptionStartedAtRef.current ?? new Date().toISOString(),
          },
        },
        onCompleted: (response) => {
          const savedId = response.createOrAppendCallTranscript?.callTranscript?.id ?? null
          callTranscriptIdRef.current = savedId
          lastSavedTranscriptIndexRef.current = allTranscripts.length
          resolve()
        },
        onError: (error) => {
          console.error("[usePhoneCallTranscription] Failed to save transcript:", error)
          reject(error)
        },
      })
    })
  }, [appointmentId, progressNoteId, commitMutation, store])

  const startTranscription = useCallback(async () => {
    if (hasStartedRef.current) {
      return
    }

    hasStartedRef.current = true

    clearTranscripts()
    transcriptionStartedAtRef.current = new Date().toISOString()
    lastSavedTranscriptIndexRef.current = 0
    callTranscriptIdRef.current = null

    try {
      await speechToText.startRecording()

      if (progressNoteId) {
        saveIntervalRef.current = setInterval(() => {
          saveTranscripts()
        }, SofiaEnvironment.SpeechToText.TranscriptSaveIntervalMs)
      }
    } catch (error) {
      console.error("[usePhoneCallTranscription] Failed to start recording:", error)
      hasStartedRef.current = false
      stopRecordingAction()
      throw error
    }
  }, [progressNoteId, clearTranscripts, speechToText, stopRecordingAction, saveTranscripts])

  const stopTranscription = useCallback(async () => {
    if (saveIntervalRef.current) {
      clearInterval(saveIntervalRef.current)
      saveIntervalRef.current = null
    }

    try {
      await saveTranscripts()
    } catch (error) {
      console.error("[usePhoneCallTranscription] Mutation failed, continuing with stop:", error)
    }

    speechToText.stopRecording()
    stopRecordingAction()

    hasStartedRef.current = false
    clearTranscripts()
    transcriptionStartedAtRef.current = null
    lastSavedTranscriptIndexRef.current = 0
    callTranscriptIdRef.current = null
  }, [speechToText, stopRecordingAction, saveTranscripts, clearTranscripts])

  const setSelectedMicrophoneId = useCallback(
    (microphoneId: string | null) => {
      setSelectedMicrophoneIdAction(microphoneId)
    },
    [setSelectedMicrophoneIdAction],
  )

  // Auto-start transcription when enabled and recording
  useEffect(() => {
    if (enabled && isRecording && appointmentId && !hasStartedRef.current) {
      startTranscription().catch((error) => {
        console.error("[usePhoneCallTranscription] Auto-start failed:", error)
      })
    }

    // Reset when recording stops
    if (!isRecording) {
      hasStartedRef.current = false
    }
  }, [enabled, isRecording, appointmentId, startTranscription])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current)
        saveIntervalRef.current = null
      }
      reset()
    }
  }, [reset])

  if (!enabled) {
    return {
      isRecording: false,
      connectionStatus: "disconnected",
      startTranscription: async () => {},
      stopTranscription: async () => {},
      transcripts: [],
      selectedMicrophoneId: null,
      setSelectedMicrophoneId: () => {},
    }
  }

  return {
    isRecording,
    connectionStatus,
    startTranscription,
    stopTranscription,
    transcripts,
    selectedMicrophoneId,
    setSelectedMicrophoneId,
  }
}
